import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import VendorLayout from '../../components/VendorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Package, ShoppingCart, DollarSign, TrendingUp, 
  Clock, CheckCircle, Truck, AlertCircle, Plus,
  Box, CreditCard, AlertTriangle, ArrowRight, Download,
  BarChart3, Eye, IndianRupee, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPrice } from '../../lib/utils';

export default function VendorDashboardEnhanced() {
  const { vendor, token, isApproved, isPending } = useVendor();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inventoryAlerts, setInventoryAlerts] = useState({ lowStock: 0, outOfStock: 0 });
  const navigate = useNavigate();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Authentication is now handled by VendorProtectedRoute
    // Only fetch data if vendor is approved
    if (vendor && isApproved) {
      fetchDashboardData();
      fetchInventoryAlerts();
    } else {
      setLoading(false);
    }
  }, [vendor, isApproved]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryAlerts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const products = response.data || [];
      const lowStock = products.filter(p => p.inventory > 0 && p.inventory <= 5).length;
      const outOfStock = products.filter(p => p.inventory === 0).length;
      setInventoryAlerts({ lowStock, outOfStock });
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
    }
  };

  // Pending Approval State
  if (isPending) {
    return (
      <VendorLayout>
        <div className="max-w-2xl mx-auto py-8">
          <Card className="border-2 border-yellow-200">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Approval Pending</h2>
              <p className="text-gray-600 mb-6">
                Your vendor application is under review. Our team will verify your details and approve within 24-48 hours.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">Application Details:</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Business Name:</span>
                    <span className="font-medium">{vendor?.business_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Owner:</span>
                    <span className="font-medium">{vendor?.owner_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <Badge className="bg-yellow-100 text-yellow-800">PENDING</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </VendorLayout>
    );
  }

  // Rejected State
  if (vendor?.status === 'rejected') {
    return (
      <VendorLayout>
        <div className="max-w-2xl mx-auto py-8">
          <Card className="border-2 border-red-200">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Application Rejected</h2>
              {vendor?.rejection_reason && (
                <div className="bg-red-50 rounded-lg p-4 text-left mb-6">
                  <h4 className="font-medium text-red-900 mb-1">Reason:</h4>
                  <p className="text-red-700">{vendor.rejection_reason}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </VendorLayout>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <VendorLayout>
      <div className="space-y-6">
        {/* To-Do List Section (Like Meesho) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-600" />
              To-do list
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/vendor/orders?status=pending" className="block">
                <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors cursor-pointer" data-testid="todo-pending-orders">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Pending Orders</p>
                    <p className="text-xl font-bold text-gray-900">{stats.pending_orders || 0}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              </Link>

              <Link to="/vendor/orders?status=packed" className="block">
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors cursor-pointer" data-testid="todo-download-labels">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Download className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Download Labels</p>
                    <p className="text-xl font-bold text-gray-900">{stats.processing_orders || 0}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              </Link>

              <Link to="/vendor/products?filter=outofstock" className="block">
                <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl hover:bg-red-100 transition-colors cursor-pointer" data-testid="todo-out-of-stock">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Out of Stock</p>
                    <p className="text-xl font-bold text-gray-900">{inventoryAlerts.outOfStock}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              </Link>

              <Link to="/vendor/products?filter=lowstock" className="block">
                <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-xl hover:bg-yellow-100 transition-colors cursor-pointer" data-testid="todo-low-stock">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Box className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Low Stock</p>
                    <p className="text-xl font-bold text-gray-900">{inventoryAlerts.lowStock}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-100">Total Revenue</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatPrice(stats.total_revenue || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <IndianRupee className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-100">Total Orders</p>
                  <p className="text-2xl font-bold mt-1">{stats.total_orders || 0}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-100">Total Products</p>
                  <p className="text-2xl font-bold mt-1">{stats.total_products || 0}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-100">Pending Payout</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatPrice(stats.pending_payout || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Business Insights (Like Meesho) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-gray-600" />
                Business Insights
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="text-xs">Daily</Button>
                <Button variant="ghost" size="sm" className="text-xs">Weekly</Button>
                <Button variant="ghost" size="sm" className="text-xs">Monthly</Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Simple placeholder chart - in real app, use recharts */}
              <div className="h-48 flex items-end justify-around gap-2 border-b border-l relative">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-400 -ml-8">
                  <span>40k</span>
                  <span>30k</span>
                  <span>20k</span>
                  <span>10k</span>
                  <span>0</span>
                </div>
                {/* Bars */}
                {[15, 25, 30, 20, 35, 28, 22].map((height, i) => (
                  <div
                    key={i}
                    className="w-8 bg-gradient-to-t from-green-500 to-emerald-400 rounded-t transition-all hover:from-green-600 hover:to-emerald-500"
                    style={{ height: `${height * 2}%` }}
                  />
                ))}
              </div>
              {/* X-axis labels */}
              <div className="flex justify-around mt-2 text-xs text-gray-500">
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
                <span>Sun</span>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Views Today</span>
                  <Eye className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-2xl font-bold">0</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Orders Today</span>
                  <ShoppingCart className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-2xl font-bold">0</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Commission Rate</span>
                  <Percent className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-2xl font-bold">{vendor?.commission?.rate || 10}%</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-800">{stats.pending_orders || 0}</p>
                <p className="text-xs text-yellow-600">Pending</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                <Package className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-800">{stats.processing_orders || 0}</p>
                <p className="text-xs text-blue-600">Processing</p>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <Truck className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-indigo-800">{stats.shipped_orders || 0}</p>
                <p className="text-xs text-indigo-600">Shipped</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl border border-green-100">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-800">{stats.delivered_orders || 0}</p>
                <p className="text-xs text-green-600">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Orders</CardTitle>
            <Link to="/vendor/orders">
              <Button variant="outline" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {dashboardData?.recent_orders?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recent_orders.map(order => (
                  <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-medium">Order #{order.id?.slice(-8).toUpperCase()}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{formatPrice(order.total)}</p>
                      <Badge className={
                        order.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.order_status === 'shipped' ? 'bg-indigo-100 text-indigo-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>
                        {order.order_status?.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No orders yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </VendorLayout>
  );
}
